import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ScrollText,
  Plus,
  Trash2,
  Calendar,
  PlayCircle,
  Lock,
  Pencil,
  Mic,
  Loader2,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, TableSkeleton } from "@/components/EmptyState";
import { UnsplashPicker } from "@/components/UnsplashPicker";
import { EntriesForm } from "@/components/EntriesForm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = {
  devotional_type_id: "",
  title: "",
  date: today(),
  thumbnail: "",
  video_url: "",
  audio_url: "",
};

// Convert backend entries shape into the editor state.
// Backend returns entries as { field_key → field_value, field_key_audio_url → url }
function entriesFromApi(entries) {
  const values = {};
  const audioUrls = {};
  if (!entries) return { values, audioUrls };
  for (const [k, v] of Object.entries(entries)) {
    if (k.endsWith("_audio_url")) {
      audioUrls[k.slice(0, -"_audio_url".length)] = v;
    } else {
      values[k] = v;
    }
  }
  return { values, audioUrls };
}

// Convert editor state into the array of entry objects the backend expects on
// POST /api/devotionals and PUT /api/devotionals/{id}
function entriesToApi(fields, values, audioUrls) {
  return fields.map((f) => ({
    field_key: f.field_key,
    field_value: values[f.field_key] ?? (f.field_type === "list" ? [] : ""),
    audio_url: audioUrls[f.field_key] || null,
  }));
}

export default function DevotionalsPage() {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Editor dialog state — shared by create + edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);    // null = create, else devotional id
  const [form, setForm] = useState(EMPTY_FORM);
  const [entryValues, setEntryValues] = useState({});
  const [entryAudios, setEntryAudios] = useState({});
  const [typeFields, setTypeFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const [d, t] = await Promise.all([
        api.get("/devotionals"),
        api.get("/devotional-types"),
      ]);
      setItems(d.data);
      setTypes(t.data);
    } catch (err) {
      toast.error(err.userMessage || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const filtered = useMemo(
    () => (typeFilter === "all" ? items : items.filter((d) => d.devotional_type_id === typeFilter)),
    [items, typeFilter]
  );
  const typeName = (id) => types.find((t) => t.id === id)?.name || "Unknown";

  // ---- Editor open/close helpers ----

  const fetchFieldsForType = useCallback(async (typeId) => {
    if (!typeId) {
      setTypeFields([]);
      return [];
    }
    setFieldsLoading(true);
    try {
      const { data } = await api.get("/devotional-fields", { params: { type_id: typeId } });
      data.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
      setTypeFields(data);
      return data;
    } catch (err) {
      toast.error(err.userMessage || "Failed to load type fields");
      setTypeFields([]);
      return [];
    } finally {
      setFieldsLoading(false);
    }
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setEntryValues({});
    setEntryAudios({});
    setTypeFields([]);
    setDialogOpen(true);
  };

  const openEdit = async (d) => {
    setEditingId(d.id);
    setDialogOpen(true);
    // Pre-populate header fields immediately so the dialog isn't blank while we
    // fetch the full devotional payload (which contains entries + per-field audio).
    setForm({
      devotional_type_id: d.devotional_type_id,
      title: d.title || "",
      date: d.date || today(),
      thumbnail: d.thumbnail || "",
      video_url: d.video_url || "",
      audio_url: d.audio_url || "",
    });
    setEntryValues({});
    setEntryAudios({});
    setTypeFields([]);
    try {
      const [{ data: full }] = await Promise.all([api.get(`/devotionals/${d.id}`)]);
      // Hydrate from backend entries
      const { values, audioUrls } = entriesFromApi(full?.entries || {});
      setEntryValues(values);
      setEntryAudios(audioUrls);
      // Use fields from the full payload to avoid a second request
      const fields = (full?.fields || []).slice().sort(
        (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
      );
      setTypeFields(fields);
    } catch (err) {
      toast.error(err.userMessage || "Failed to load devotional");
    }
  };

  // When the type is switched inside the create dialog, fetch its fields and
  // reset entry values. We do NOT reset on first open because Edit pre-loads
  // them above.
  const handleTypeChange = async (typeId) => {
    setForm((f) => ({ ...f, devotional_type_id: typeId }));
    if (editingId) {
      // Type-switch on edit is unusual but allowed — keep existing entries to
      // avoid silent data loss; admin can clear individual fields if needed.
      await fetchFieldsForType(typeId);
    } else {
      setEntryValues({});
      setEntryAudios({});
      await fetchFieldsForType(typeId);
    }
  };

  const validateRequired = () => {
    for (const f of typeFields) {
      if (!f.required) continue;
      const v = entryValues[f.field_key];
      const empty =
        v === undefined ||
        v === null ||
        (typeof v === "string" && v.trim() === "") ||
        (Array.isArray(v) && (v.length === 0 || v.every((x) => !x || x.trim() === "")));
      if (empty) return f.field_name;
    }
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.devotional_type_id) {
      toast.error("Pick a devotional type first.");
      return;
    }
    const missing = validateRequired();
    if (missing) {
      toast.error(`"${missing}" is required.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        devotional_type_id: form.devotional_type_id,
        title: form.title,
        date: form.date,
        thumbnail: form.thumbnail || null,
        video_url: form.video_url || null,
        audio_url: form.audio_url || null,
        entries: entriesToApi(typeFields, entryValues, entryAudios),
      };
      if (editingId) {
        await api.put(`/devotionals/${editingId}`, payload);
        toast.success("Devotional updated");
      } else {
        await api.post("/devotionals", payload);
        toast.success("Devotional published");
      }
      setDialogOpen(false);
      await reload();
    } catch (err) {
      toast.error(err.userMessage || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/devotionals/${deleteTarget.id}`);
      toast.success(`Deleted "${deleteTarget.title}"`);
      await reload();
    } catch (err) {
      toast.error(err.userMessage || "Delete failed");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Devotionals"
        description="All devotional posts. Filter by type or publish a new one."
        actions={
          <Button onClick={openCreate} disabled={types.length === 0} data-testid="add-devotional-button">
            <Plus className="size-4 mr-1.5" /> New devotional
          </Button>
        }
      />

      {/* Filter chips */}
      {!loading && types.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-5" data-testid="devotional-type-filter">
          <button
            type="button"
            onClick={() => setTypeFilter("all")}
            className={
              "px-3 py-1.5 text-xs font-medium rounded-md border transition-colors " +
              (typeFilter === "all"
                ? "bg-foreground text-background border-foreground"
                : "bg-background border-border text-muted-foreground hover:text-foreground")
            }
          >
            All ({items.length})
          </button>
          {types.map((t) => {
            const count = items.filter((d) => d.devotional_type_id === t.id).length;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTypeFilter(t.id)}
                className={
                  "px-3 py-1.5 text-xs font-medium rounded-md border transition-colors " +
                  (typeFilter === t.id
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background border-border text-muted-foreground hover:text-foreground")
                }
              >
                {t.name} ({count})
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <Card className="p-4">
          <TableSkeleton rows={6} cols={3} />
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title={items.length === 0 ? "No devotionals yet" : "No devotionals in this type"}
          description={
            items.length === 0
              ? "Create your first devotional to share with your audience."
              : "Switch to a different type or create one."
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((d) => (
            <Card
              key={d.id}
              data-testid={`devotional-card-${d.id}`}
              className="overflow-hidden border-border/70 shadow-sm hover:shadow-md transition-shadow group"
            >
              <div className="relative aspect-[16/9] bg-muted">
                {d.thumbnail ? (
                  <img
                    src={d.thumbnail}
                    alt={d.title}
                    className="size-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                  />
                ) : (
                  <div className="size-full flex items-center justify-center text-muted-foreground">
                    <ScrollText className="size-10" />
                  </div>
                )}
                {d.video_url && (
                  <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-medium backdrop-blur">
                    <PlayCircle className="size-3" /> video
                  </div>
                )}
                {d.is_premium && (
                  <Badge className="absolute top-2 left-2 bg-accent text-accent-foreground border-transparent flex items-center gap-1">
                    <Lock className="size-3" /> Premium
                  </Badge>
                )}
                <Badge className="absolute top-2 right-2 capitalize" variant="secondary">
                  {typeName(d.devotional_type_id)}
                </Badge>
              </div>
              <div className="p-4">
                <h3 className="font-semibold tracking-tight line-clamp-1">{d.title}</h3>
                <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="size-3.5" />
                  {d.date}
                </div>
                <div className="mt-3 flex justify-end gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(d)}
                    data-testid={`devotional-edit-${d.id}`}
                  >
                    <Pencil className="size-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setDeleteTarget(d)}
                    data-testid={`devotional-delete-${d.id}`}
                  >
                    <Trash2 className="size-3.5 mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog ----------------------------------------------- */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(v) => {
          if (!v && !saving) setDialogOpen(false);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={onSubmit}>
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit devotional" : "Create devotional"}</DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update the metadata and content of this devotional."
                  : "Pick a type, fill the metadata, then complete the content fields below."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 py-4">
              {/* --- Meta section --- */}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select
                    value={form.devotional_type_id}
                    onValueChange={handleTypeChange}
                    disabled={!!editingId}
                  >
                    <SelectTrigger data-testid="devotional-type-select">
                      <SelectValue placeholder="Select a type…" />
                    </SelectTrigger>
                    <SelectContent>
                      {types.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editingId && (
                    <p className="text-[10px] text-muted-foreground">
                      Type can't be changed after creation to avoid orphaning entries.
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="d-title">Title</Label>
                  <Input
                    id="d-title"
                    data-testid="devotional-title-input"
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="d-date">Date</Label>
                    <Input
                      id="d-date"
                      data-testid="devotional-date-input"
                      type="date"
                      required
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Used by the mobile home to surface this devotional on its date.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="d-thumb">Thumbnail URL</Label>
                      <UnsplashPicker onSelect={(url) => setForm({ ...form, thumbnail: url })} />
                    </div>
                    <Input
                      id="d-thumb"
                      data-testid="devotional-thumbnail-input"
                      placeholder="https://images.unsplash.com/photo-…"
                      value={form.thumbnail}
                      onChange={(e) => setForm({ ...form, thumbnail: e.target.value })}
                    />
                    {form.thumbnail && (
                      <div className="mt-1 rounded-md border border-border overflow-hidden bg-muted aspect-[16/9] max-w-[16rem]">
                        <img
                          src={form.thumbnail}
                          alt="Thumbnail preview"
                          className="size-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="d-video">Video URL (optional)</Label>
                  <Input
                    id="d-video"
                    placeholder="https://…"
                    value={form.video_url}
                    onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="d-audio" className="inline-flex items-center gap-1.5">
                    <Mic className="size-3.5" />
                    Whole-devotional audio URL (optional)
                  </Label>
                  <Input
                    id="d-audio"
                    type="url"
                    data-testid="devotional-audio-input"
                    placeholder="https://…/devotional.mp3"
                    value={form.audio_url || ""}
                    onChange={(e) => setForm({ ...form, audio_url: e.target.value })}
                  />
                </div>
              </div>

              {/* --- Content section --- */}
              <div className="pt-4 border-t border-border">
                <div className="mb-3">
                  <h4 className="text-sm font-semibold">Content</h4>
                  <p className="text-xs text-muted-foreground">
                    These fields come from the selected type. Edit them in{" "}
                    <strong>Devotional Types → Edit</strong> to change the structure.
                  </p>
                </div>

                {!form.devotional_type_id ? (
                  <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                    Select a devotional type above to load its fields.
                  </div>
                ) : fieldsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" /> Loading fields…
                  </div>
                ) : (
                  <EntriesForm
                    fields={typeFields}
                    values={entryValues}
                    audioUrls={entryAudios}
                    onChange={(values, audios) => {
                      setEntryValues(values);
                      setEntryAudios(audios);
                    }}
                  />
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving || !form.devotional_type_id}
                data-testid="devotional-submit-button"
              >
                {saving ? "Saving…" : editingId ? "Save changes" : "Publish"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>This also removes all entries attached to it.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              data-testid="confirm-delete-devotional"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
